import React from 'react';
import allCountries from 'country-region-data/data.json';
import { ChevronDownRegular } from '@fluentui/react-icons';
import { useLocalization } from '../Helpers/useLocalization';
import AddressSuggestInput from './AddressSuggestInput';

const QUICK_COUNTRIES = ['United States', 'Canada', 'Australia', 'United Kingdom'];

const COUNTRY_SEARCH_INDEX = allCountries.map(c => ({
    name: c.countryName,
    nameLower: c.countryName.toLowerCase(),
    codeLower: c.countryShortCode.toLowerCase(),
}));

interface ICountrySelection {
    value?: string;
    onChange?: (value: string) => void;
    disabled?: boolean;
    placeholder?: string;
}

const inputStyle: React.CSSProperties = {
    width: '100%',
    boxSizing: 'border-box',
    paddingLeft: '8px',
    paddingRight: '28px',
    paddingTop: '8px',
    paddingBottom: '8px',
    height: '32px',
    border: '1px solid rgb(96,94,92)',
    borderRadius: '2px',
};

const CountrySelection: React.FC<ICountrySelection> = (props) => {
    const L = useLocalization();
    const [quickOpen, setQuickOpen] = React.useState(false);
    const [focusedQuickIndex, setFocusedQuickIndex] = React.useState(-1);
    const containerRef = React.useRef<HTMLDivElement>(null);

    React.useEffect(() => {
        if (!quickOpen) return;
        const handleClickOutside = (e: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
                setQuickOpen(false);
                setFocusedQuickIndex(-1);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [quickOpen]);

    // Matches on name or ISO code, so "gb" finds United Kingdom.
    const getSuggestions = React.useCallback((query: string) => {
        const q = query.toLowerCase();
        return COUNTRY_SEARCH_INDEX
            .filter(c => c.nameLower.startsWith(q) || c.codeLower.startsWith(q))
            .map(c => c.name);
    }, []);

    // Claims the arrows and Enter only while the quick-select menu is open, so the suggestion list keeps
    // them the rest of the time.
    const handleQuickKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (!quickOpen) return;
        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setFocusedQuickIndex(i => Math.min(i + 1, QUICK_COUNTRIES.length - 1));
                break;
            case 'ArrowUp':
                e.preventDefault();
                setFocusedQuickIndex(i => Math.max(i - 1, 0));
                break;
            case 'Enter':
                if (focusedQuickIndex >= 0) {
                    e.preventDefault();
                    props.onChange?.(QUICK_COUNTRIES[focusedQuickIndex]);
                    setQuickOpen(false);
                    setFocusedQuickIndex(-1);
                }
                break;
            case 'Escape':
                e.preventDefault();
                setQuickOpen(false);
                setFocusedQuickIndex(-1);
                break;
        }
    };

    return (
        <div ref={containerRef} style={{ position: 'relative', width: '100%' }}>
            <AddressSuggestInput
                value={props.value}
                disabled={props.disabled}
                placeholder={props.placeholder ?? L('localization_address_country_placeholder')}
                inputStyle={inputStyle}
                getSuggestions={getSuggestions}
                onInputKeyDown={handleQuickKeyDown}
                onChange={(next) => {
                    props.onChange?.(next);
                    if (next) {
                        setQuickOpen(false);
                        setFocusedQuickIndex(-1);
                    }
                }}
                adornment={
                    <button
                        type='button'
                        onMouseDown={(e) => {
                            e.preventDefault();
                            if (!props.disabled) { setQuickOpen(o => !o); setFocusedQuickIndex(-1); }
                        }}
                        style={{
                            background: 'none', border: 'none',
                            cursor: props.disabled ? 'default' : 'pointer',
                            padding: '0 2px', display: 'flex', alignItems: 'center', color: '#616161',
                        }}
                        aria-label='Quick select'
                    ><ChevronDownRegular fontSize={16} /></button>
                }
            />
            {quickOpen && (
                <div className='country-quick-select' style={{
                    position: 'absolute', top: '100%', left: 0, zIndex: 20,
                    background: '#fff', border: '1px solid #aaa', borderRadius: '4px',
                    minWidth: '100%', boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
                    marginTop: '2px',
                }}>
                    {QUICK_COUNTRIES.map((c, i) => (
                        <div
                            key={c}
                            onMouseDown={(e) => { e.preventDefault(); props.onChange?.(c); setQuickOpen(false); setFocusedQuickIndex(-1); }}
                            onMouseEnter={() => setFocusedQuickIndex(i)}
                            onMouseLeave={() => setFocusedQuickIndex(-1)}
                            style={{
                                padding: '8px 12px', cursor: 'pointer', fontSize: '14px',
                                background: focusedQuickIndex === i ? '#e8f0fe' : 'transparent',
                                outline: focusedQuickIndex === i ? '2px solid #0078d4' : 'none',
                                outlineOffset: '-2px',
                            }}
                        >{c}</div>
                    ))}
                </div>
            )}
        </div>
    );
}

export default CountrySelection;
